// The current workspace on this device (spec v3 §1.2). `*` means Everything.
import { create } from 'zustand'
import { db } from '@/runtime'
import { META } from '@/db/schema'
import { ALL, DEFAULT_WORKSPACE_SLUG } from '@/kinds/workspace/schema'

interface ScopeState {
  ws: string
  /**
   * The suite to fall back to: the default one when this device sees it, else
   * the first suite it does see. A member's device sees only the suites her
   * grant covers, so the owner's default is never hers.
   */
  home: string
  setWs(ws: string): void
}

export const useScope = create<ScopeState>((set) => ({
  ws: DEFAULT_WORKSPACE_SLUG,
  home: DEFAULT_WORKSPACE_SLUG,
  setWs(ws) {
    set({ ws })
    void db.setMeta(META.workspace, ws)
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href)
      if (url.searchParams.has('ws')) {
        url.searchParams.delete('ws')
        window.history.replaceState(null, '', url.toString())
      }
    }
  },
}))

/** A live suite, as far as choosing a scope goes. */
export interface KnownSuite {
  slug: string
  order?: number
}

/** The default suite when it is among the known ones (or nothing is known yet), else the first by order. */
export const homeSlugOf = (known: KnownSuite[]): string => {
  if (known.some((w) => w.slug === DEFAULT_WORKSPACE_SLUG)) return DEFAULT_WORKSPACE_SLUG
  const first = [...known].sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.slug.localeCompare(b.slug))[0]
  return first?.slug ?? DEFAULT_WORKSPACE_SLUG
}

/** The scope to start in: the wanted one when it is Everything or a known suite, else home. */
export const pickScope = (want: string | null | undefined, known: KnownSuite[]): string => {
  if (want && (want === ALL || known.some((w) => w.slug === want))) return want
  return homeSlugOf(known)
}

/** Restore from the URL (`?ws=`) or meta. Unknown slugs fall back to home (`homeSlugOf`). */
export const hydrateScope = async (known: KnownSuite[]) => {
  const fromUrl = typeof window !== 'undefined' ? new URL(window.location.href).searchParams.get('ws') : null
  const stored = await db.getMeta<string>(META.workspace)
  const ws = pickScope(fromUrl ?? stored, known)
  useScope.setState({ ws, home: homeSlugOf(known) })
  if (fromUrl) await db.setMeta(META.workspace, ws)
}

/** The suite a new thing lands in: the current one, or home in Everything. */
export const writeSlug = (): string => {
  const { ws, home } = useScope.getState()
  return ws === ALL ? home : ws
}

/** True when a resource with label ws=slug is in scope. */
export const inScope = (scope: string, slug: string | undefined) => scope === ALL || slug === scope
